import type { Client } from '@urql/core'
import { runInAction } from 'mobx'
import { graphql } from 'app/generated/gql'
import type { Activity as ActivityType, ActivityListResult } from 'app/generated/graphql'
import { EntityStore } from './EntityStore'

const ACTIVITY_FIELDS = `activityDate archived channel contactName contactPhone contacts { id lifecycleStage locationId name peopleIndex phone } createdDate data description fbclid gclid id isQualified locationId nameFromActivity needsReview note orgId owner { id name userId } phoneFromActivity referringUrl source subType tags trackingNumber trackingNumberName type typeId updatedDate utmCampaign utmContent utmKeywords utmMedium utmSource vendor version workspaceId`

export const ACTIVITIES_QUERY = graphql(`
  query Activities($search: String, $pagination: PaginationInput, $sort: SortInput) {
    activities(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        activityDate
        archived
        channel
        contactName
        contactPhone
        contacts {
          id
          lifecycleStage
          locationId
          name
          peopleIndex
          phone
        }
        createdDate
        data
        description
        fbclid
        gclid
        id
        isQualified
        locationId
        nameFromActivity
        needsReview
        note
        orgId
        owner {
          id
          name
          userId
        }
        phoneFromActivity
        referringUrl
        source
        subType
        tags
        trackingNumber
        trackingNumberName
        type
        typeId
        updatedDate
        utmCampaign
        utmContent
        utmKeywords
        utmMedium
        utmSource
        vendor
        version
        workspaceId
      }
      total
      page
    }
  }
`)

const GET_QUERY = graphql(`
  query Activity($id: ID!) {
    activity(id: $id) {
      activityDate
      archived
      channel
      contactName
      contactPhone
      contacts {
        id
        lifecycleStage
        locationId
        name
        peopleIndex
        phone
      }
      createdDate
      data
      description
      fbclid
      gclid
      id
      isQualified
      locationId
      nameFromActivity
      needsReview
      note
      orgId
      owner {
        id
        name
        userId
      }
      phoneFromActivity
      referringUrl
      source
      subType
      tags
      trackingNumber
      trackingNumberName
      type
      typeId
      updatedDate
      utmCampaign
      utmContent
      utmKeywords
      utmMedium
      utmSource
      vendor
      version
      workspaceId
    }
  }
`)

const DELETE_MUTATION = graphql(`
  mutation DeleteActivity($id: ID!) {
    deleteActivity(id: $id)
  }
`)

export const UPDATE_NOTE_MUTATION = graphql(`
  mutation UpdateNote($id: ID!, $input: UpdateNoteInput!) {
    updateNote(id: $id, input: $input) {
      activityDate
      archived
      channel
      contactName
      contactPhone
      contacts {
        id
        lifecycleStage
        locationId
        name
        peopleIndex
        phone
      }
      createdDate
      data
      description
      fbclid
      gclid
      id
      isQualified
      locationId
      nameFromActivity
      needsReview
      note
      orgId
      owner {
        id
        name
        userId
      }
      phoneFromActivity
      referringUrl
      source
      subType
      tags
      trackingNumber
      trackingNumberName
      type
      typeId
      updatedDate
      utmCampaign
      utmContent
      utmKeywords
      utmMedium
      utmSource
      vendor
      version
      workspaceId
    }
  }
`)

export const UPDATE_MILESTONE_MUTATION = graphql(`
  mutation UpdateMilestone($id: ID!, $input: UpdateMilestoneInput!) {
    updateMilestone(id: $id, input: $input) {
      activityDate
      archived
      channel
      contactName
      contactPhone
      contacts {
        id
        lifecycleStage
        locationId
        name
        peopleIndex
        phone
      }
      createdDate
      data
      description
      fbclid
      gclid
      id
      isQualified
      locationId
      nameFromActivity
      needsReview
      note
      orgId
      owner {
        id
        name
        userId
      }
      phoneFromActivity
      referringUrl
      source
      subType
      tags
      trackingNumber
      trackingNumberName
      type
      typeId
      updatedDate
      utmCampaign
      utmContent
      utmKeywords
      utmMedium
      utmSource
      vendor
      version
      workspaceId
    }
  }
`)

export const REASSIGN_ACTIVITY_MUTATION = graphql(`
  mutation ReassignActivity($id: ID!, $input: ReassignActivityInput!) {
    reassignActivity(id: $id, input: $input) {
      activityDate
      archived
      channel
      contactName
      contactPhone
      contacts {
        id
        lifecycleStage
        locationId
        name
        peopleIndex
        phone
      }
      createdDate
      data
      description
      fbclid
      gclid
      id
      isQualified
      locationId
      nameFromActivity
      needsReview
      note
      orgId
      owner {
        id
        name
        userId
      }
      phoneFromActivity
      referringUrl
      source
      subType
      tags
      trackingNumber
      trackingNumberName
      type
      typeId
      updatedDate
      utmCampaign
      utmContent
      utmKeywords
      utmMedium
      utmSource
      vendor
      version
      workspaceId
    }
  }
`)

export class ActivityStore extends EntityStore<ActivityType, ActivityListResult> {
  constructor(client: Client) {
    super(client, {
      listQuery: ACTIVITIES_QUERY,
      getQuery: GET_QUERY,
      deleteMutation: DELETE_MUTATION,
      typename: `Activity`,
      extractList: (data: any) => data.activities,
      extractOne: (data: any) => data.activity,
      extractMutated: (data: any) => data.updateNote || data.updateMilestone || data.reassignActivity,
    })
  }

  /** Set an activity's staff owner in the list at once, and put the previous owner back if the save fails. */
  async reassignOwner(id: string, owner?: ActivityType['owner']) {
    const activity = this.lookup(id)
    const previous = activity?.owner
    const setOwner = (next: ActivityType['owner']) =>
      runInAction(() => {
        if (activity) activity.owner = next
        if (this.list) this.list = { ...this.list, hits: [...this.list.hits] }
      })
    setOwner(owner)
    const result = await this.client
      .mutation(REASSIGN_ACTIVITY_MUTATION, {
        id,
        input: { ownerId: owner?.id ?? `-1`, ownerName: owner?.name ?? `-` },
      })
      .toPromise()
    if (result.error) setOwner(previous)
    else if (result.data) runInAction(() => this.intern(result.data!.reassignActivity as ActivityType))
    return result
  }
}
